import { memo } from 'react';
import { CalendarClock, LogIn, LogOut, Pin, Video } from 'lucide-react';
import { cn } from '@cyphex/ui';
import type { Message } from '@cyphex/types';
import { formatDayDivider } from '../../lib/time';

interface DayDividerProps {
  date: string;
}

/**
 * `Today` / `Friday` / `Wednesday, 12 February` across the thread.
 *
 * Built inline rather than through the `Divider` primitive so the label can be
 * the small semibold stamp Teams uses. The primitive's label is body-sized, and
 * at the rate a chat produces separators that reads as content rather than as
 * chrome. `<time>` carries the machine-readable date for assistive tech.
 */
export const DayDivider = memo(function DayDivider({ date }: DayDividerProps) {
  return (
    <div role="separator" className="flex items-center gap-3 px-4 py-2">
      <span className="h-px flex-1 bg-border" />
      <time
        dateTime={date.slice(0, 10)}
        className="shrink-0 text-xs font-semibold text-muted-foreground"
      >
        {formatDayDivider(date)}
      </time>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
});

interface UnreadDividerProps {
  count: number;
}

export const UnreadDivider = memo(function UnreadDivider({ count }: UnreadDividerProps) {
  return (
    <div className="flex items-center gap-2 px-4 py-1.5" aria-label={`${count} unread messages`}>
      <span className="h-px flex-1 bg-destructive/50" />
      <span className="text-2xs font-semibold uppercase tracking-wide text-destructive">
        {count} new {count === 1 ? 'message' : 'messages'}
      </span>
      <span className="h-px flex-1 bg-destructive/50" />
    </div>
  );
});

interface SystemMessageProps {
  message: Message;
  className?: string;
}

const EVENT_ICON = {
  'member.joined': LogIn,
  'member.left': LogOut,
  'member.removed': LogOut,
  'meeting.started': Video,
  'meeting.ended': Video,
  'pin.added': Pin,
} as const;

/** Join/leave/pin/meeting events — a glyph and one muted line, never a bubble. */
export const SystemMessage = memo(function SystemMessage({ message, className }: SystemMessageProps) {
  const event = message.systemEvent?.event;
  const Icon = (event && event in EVENT_ICON ? EVENT_ICON[event as keyof typeof EVENT_ICON] : CalendarClock);

  return (
    <p className={cn('flex items-center justify-center gap-1.5 px-4 py-1 text-sm text-muted-foreground', className)}>
      <Icon size={14} strokeWidth={2.25} aria-hidden />
      {/* `||`, NOT `??`. A system message sent with no text stores `body_text`
          as the EMPTY STRING, not null (lib/richtext.ts `normaliseBody`), and
          `?? ` lets an empty string through — which is what `conversation
          .renamed` has been doing since it shipped: an icon, and a blank line
          where "Ganesh renamed this chat" should be. */}
      {message.bodyText || 'Conversation updated'}
    </p>
  );
});
